import CallModal from 'app/components/call/CallModal';
import CallModalMascot from 'app/components/call/CallModalMascot';
import ReloadButton from 'app/components/call/ReloadButton';
import TroubleshootingLink from 'app/components/call/TroubleshootingLink';
import BaseButton from 'app/components/common/BaseButton';
import FlexMarginGroup from 'app/components/layout/FlexMarginGroup';
import { useI18n } from 'app/components/localization';
import ButtonClassNameA, { ButtonA, ColorScheme, Size } from 'app/components/presentation/buttons/ButtonClassNameA';
import LoadingButtonA from 'app/components/presentation/buttons/LoadingButtonA';
import { getLinkStyles } from 'app/components/presentation/link/styles';
import MascotWithStopSign from 'app/components/svgs/mascot/CarryingStopSign';
import MascotWaving from 'app/components/svgs/mascot/Waving';
import { MARGIN_A } from 'app/constants/Styles';
import { CallStage, useCallStateContext } from 'app/containers/call/CallStateContext';
import { useBookingState } from 'app/containers/rtc/BookingContext/BookingState';
import { LoadingStage, useLoadingState } from 'app/containers/state/LoadingState';
import { BookingType } from 'app/models/rtc/booking/constants';
import { useCss } from 'app/utils/css';
import React, { Fragment, FunctionComponent, memo, useState } from 'react';
import { LessonButtonStage } from 'app/models/ui/call/lesson/constants';

interface IModalProps {
  hideModal: () => void;
  endLesson: () => Promise<void>;
  inProgress: boolean;
}

/**
 * Modal meant to be shown when the user is meant to actually end
 * the lesson, past the lesson end time
 */
const EndLessonPastLessonTimeModal: FunctionComponent<IModalProps> = ({ hideModal, endLesson, inProgress }) => {
  const { t, tx } = useI18n();
  const css = useCss();

  return (
    <CallModal
      title={t('lesson-booking-details.end-lesson.title')}
      buttons={
        <FlexMarginGroup margin={MARGIN_A} center wrap>
          <ButtonA onClick={hideModal} colorScheme={ColorScheme.Secondary}>
            {t('lesson-booking-details.end-lesson.cancel')}
          </ButtonA>
          <LoadingButtonA onClick={endLesson} isLoading={inProgress} disabled={inProgress}>
            {t('lesson-booking-details.end-lesson.confirm')}
          </LoadingButtonA>
        </FlexMarginGroup>
      }
      onClose={hideModal}
    >
      <div className={css('container', { display: 'flex', flexDirection: 'column' })}>
        <CallModalMascot>
          <MascotWaving width="100%" />
        </CallModalMascot>

        <div>
          <p>{tx('lesson-booking-details.end-lesson.warning')}</p>
        </div>
      </div>
    </CallModal>
  );
};

const EndLessonEarlyModal: FunctionComponent<IModalProps> = ({ hideModal, endLesson, inProgress }) => {
  const { t, tx } = useI18n();
  const css = useCss();

  return (
    <CallModal
      title={t('lesson-booking-details.end-lesson-early.title')}
      onClose={hideModal}
      buttons={
        <FlexMarginGroup margin={MARGIN_A} center vertical>
          <ButtonClassNameA colorScheme={ColorScheme.Secondary}>{(buttonClassName) =>
            <ReloadButton className={buttonClassName}>
              {t('lesson-booking-details.end-lesson-early.reload')}
            </ReloadButton>
          }</ButtonClassNameA>
          <BaseButton className={css('link', getLinkStyles())} onClick={endLesson} disabled={inProgress}>
            {t('lesson-booking-details.end-lesson-early.end-for-sure')}
          </BaseButton>
        </FlexMarginGroup>
      }
    >
      <div className={css('container', { display: 'flex', flexDirection: 'column' })}>
        <CallModalMascot>
          <MascotWithStopSign width="100%" />
        </CallModalMascot>

        <div>
          <p className={css('prematureWarning', { fontWeight: 600 })}>
            {t('lesson-booking-details.end-lesson-early.warning')}
          </p>

          <p>
            {tx('lesson-booking-details.end-lesson-early.problems', {
              troubleshoot: (
                <TroubleshootingLink className={css('link', getLinkStyles())} target="_blank">
                  {t('lesson-booking-details.end-lesson-early.troubleshoot')}
                </TroubleshootingLink>
              ),
            })}
          </p>
        </div>
      </div>
    </CallModal>
  );
};

interface IProps {
  variant: LessonButtonStage.ShowEnd | LessonButtonStage.ShowEndUrgent;
}

interface IDeps {
  disabled: boolean;
  endLesson: () => any;
  inProgress: boolean;
}

const EndLessonButton = memo<IDeps & IProps>((props) => {
  const [isOpen, setIsOpen] = useState(false);

  const {
    endLesson,
    inProgress,
    disabled,
    variant,
  } = props;

  const { t } = useI18n();
  const css = useCss();

  const showModal = () => {
    setIsOpen(true);
  };

  const hideModal = () => {
    setIsOpen(false);
  };

  const modalProps = { hideModal, endLesson, inProgress };

  return (
    <Fragment>
      {variant === LessonButtonStage.ShowEnd ? (
        <div className={css('linkContainer', { whiteSpace: 'nowrap', padding: '0 1em' })}>
          <BaseButton className={css('link', getLinkStyles())} onClick={showModal} disabled={disabled}>
            {t('lesson-booking-details.end-lesson-early.button')}
          </BaseButton>
        </div>
      ) : (
        <ButtonA pulse onClick={showModal} colorScheme={ColorScheme.Red} disabled={disabled} size={Size.Tiny}>
          {t('lesson-booking-details.end-lesson.button')}
        </ButtonA>
      )}

      {!disabled && isOpen && (
        variant === LessonButtonStage.ShowEnd
        ? <EndLessonEarlyModal {...modalProps} />
        : <EndLessonPastLessonTimeModal {...modalProps} />
      )}
    </Fragment>
  );
});

const EndLessonButtonInjectDependencies: FunctionComponent<IProps> = (props) => {
  const { lock, stage } = useCallStateContext();
  const bookingState = useBookingState();
  const loadingState = useLoadingState({ load: lock });

  return (
    <EndLessonButton
      disabled={!bookingState || bookingState.bookingType !== BookingType.Lesson || stage !== CallStage.Active}
      endLesson={loadingState.load}
      inProgress={loadingState.stage === LoadingStage.Loading}
      {...props}
    />
  );
};

export default EndLessonButtonInjectDependencies;
